import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';
import { Loader } from 'lucide-react';
import { useIntl } from 'react-intl';

const DisbursementsContext = React.createContext();

const DisbursementsProvider = props => {
  const { children } = props;
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [verificationToken, setVerificationToken] = useState(null);
  const [verificationTokenExpiry, setVerificationTokenExpiry] = useState(null);
  const [redeemToken, setRedeemToken] = useState(null);
  const [redeemTokenExpiry, setRedeemTokenExpiry] = useState(null);
  const [error, setError] = useState(null);
  const [disbursementData, setdisbursementData] = useState(null);
  const [transferSuccessData, settransferSuccessData] = useState(null);
  const [transferOptions, setTransferOptions] = useState(null);

  useEffect(() => {
    if (
      verificationToken ||
      redeemToken ||
      disbursementData ||
      transferSuccessData ||
      transferOptions
    ) {
      sessionStorage.setItem(
        'disbursementData',
        JSON.stringify({
          verificationToken,
          verificationTokenExpiry,
          redeemToken,
          redeemTokenExpiry,
          disbursementData,
          transferSuccessData,
          transferOptions,
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    verificationToken,
    verificationTokenExpiry,
    redeemToken,
    redeemTokenExpiry,
    disbursementData,
    transferSuccessData,
    transferOptions,
  ]);
  useEffect(() => {
    // load from local storage
    const stored = sessionStorage.getItem('disbursementData');
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        setVerificationToken(parsed.verificationToken);
        setVerificationTokenExpiry(parsed.verificationTokenExpiry);
        setRedeemToken(parsed.redeemToken);
        setRedeemTokenExpiry(parsed.redeemTokenExpiry);
        setdisbursementData(parsed.disbursementData);
        settransferSuccessData(parsed.transferSuccessData);
        setTransferOptions(parsed.transferOptions);
      } catch (e) {
        // ignore
      } finally {
        setLoading(false);
      }
    } else {
      setLoading(false);
    }
    // if location is not /disbursements/transfer-options, remove sessionStorage.removeItem('x-guard-flow');
    if (location.pathname !== '/disbursements/transfer-options') {
      sessionStorage.removeItem('x-guard-flow');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkTokenExpiry = () => {
    const now = Date.now();

    const isVerificationExpired = verificationTokenExpiry && now > verificationTokenExpiry;
    const isRedeemExpired = redeemTokenExpiry && now > redeemTokenExpiry;

    if (isVerificationExpired) {
      setVerificationToken(null);
      setVerificationTokenExpiry(null);
      setRedeemToken(null);
      setRedeemTokenExpiry(null);
      setdisbursementData(null);
      settransferSuccessData(null);
      setTransferOptions(null);
      navigate('/disbursements/redeem', { replace: true });
    } else if (isRedeemExpired) {
      // eslint-disable-next-line
      alert(intl.messages['session.expired']);
      setRedeemToken(null);
      setRedeemTokenExpiry(null);
      setdisbursementData(null);
      settransferSuccessData(null);
      setTransferOptions(null);
      navigate('/disbursements/verification', { replace: true });
    }
  };

  const resetSession = () => {
    setVerificationToken(null);
    setVerificationTokenExpiry(null);
    setRedeemToken(null);
    setRedeemTokenExpiry(null);
    setdisbursementData(null);
    settransferSuccessData(null);
    setTransferOptions(null);
    sessionStorage.removeItem('disbursementData');
  };

  const clearSession = () => {
    setVerificationToken(null);
    setVerificationTokenExpiry(null);
    setRedeemToken(null);
    setRedeemTokenExpiry(null);
    setdisbursementData(null);
    settransferSuccessData(null);
    setTransferOptions(null);
    sessionStorage.removeItem('disbursementData');
    navigate('/', { replace: true });
  };
  // handle token expiry validation
  useEffect(() => {
    let isMounted = true;

    const checkTokenExpiryIfMounted = () => {
      if (!isMounted) return;
      checkTokenExpiry();
    };

    if (verificationToken || redeemToken) {
      checkTokenExpiryIfMounted(); // check token expiry on load
    }
    const interval = setInterval(checkTokenExpiryIfMounted, 60000); // Check every minute
    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [verificationToken, redeemToken]);

  return (
    <DisbursementsContext.Provider
      value={{
        verificationToken,
        verificationTokenExpiry,
        redeemToken,
        redeemTokenExpiry,
        error,
        setVerificationToken,
        setVerificationTokenExpiry,
        setRedeemToken,
        setRedeemTokenExpiry,
        setError,
        transferSuccessData,
        settransferSuccessData,
        disbursementData,
        setdisbursementData,
        transferOptions,
        setTransferOptions,
        clearSession,
        resetSession,
      }}
    >
      {loading ? <Loader /> : children}
    </DisbursementsContext.Provider>
  );
};

DisbursementsProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export { DisbursementsContext, DisbursementsProvider };
